import React from 'react';
import { useTranslation } from 'react-i18next';
import { H4, P, Button } from 'design-system-web';
import { SearchBar } from 'components';
import type { VisitorConfigurationTabProps } from '../../types';
import { CustomFieldsListBody } from './CustomFieldsListBody';
import { DisableCustomFieldConfirmationModal } from './DisableCustomFieldConfirmationModal';
import { NO_ORGANIZATION_KEY } from './customFieldsTabModelUtils';
import {
  useCustomFieldsTabModel,
  type CustomFieldsListLayoutProps,
} from './useCustomFieldsTabModel';

import styles from './styles.scss';

function CustomFieldsListLayout({
  searchValue,
  onSearchChange,
  filteredRows,
  isListLoading,
  configsCalled,
  configsError,
  onCreateClick,
  onSelectRow,
  onToggleStatus,
  isStatusUpdateLoading,
}: CustomFieldsListLayoutProps): React.ReactElement {
  const { t } = useTranslation();

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div>
          <H4>{t('web.admin.visitorManagement.configuration.tab.customFields')}</H4>
          <div className={styles.description}>
            <P>{t('web.admin.visitorManagement.configuration.tab.customFields.description')}</P>
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionToolbar}>
          <SearchBar
            className={styles.searchBlock}
            searchOptions={{ search: searchValue }}
            onSearchChange={onSearchChange}
            searchInputPlaceholder={t(
              'web.admin.visitorManagement.customField.search.placeholder'
            )}
            testId="custom-field-search"
          />
          <div className={styles.sectionToolbarAction}>
            <Button variant="primary" onClick={onCreateClick}>
              {t('web.admin.visitorManagement.customField.createButton')}
            </Button>
          </div>
        </div>
        <CustomFieldsListBody
          isListLoading={isListLoading}
          configsCalled={configsCalled}
          configsError={configsError}
          tableRows={filteredRows}
          onSelectRow={onSelectRow}
          onCreateClick={onCreateClick}
          onToggleStatus={onToggleStatus}
          isStatusUpdateLoading={isStatusUpdateLoading}
          t={t}
        />
      </div>
    </div>
  );
}

export function VisitorManagementCustomFieldsTab({
  channel,
  config: _config,
  onConfigUpdated: _onConfigUpdated,
  loading: _loading,
  showValidationErrors: _showValidationErrors,
}: VisitorConfigurationTabProps): React.ReactElement {
  const { t } = useTranslation();
  const {
    navigateToCreate,
    navigateToDetail,
    searchValue,
    setSearchValue,
    filteredRows,
    isListLoading,
    configsCalled,
    configsError,
    handleToggleStatus,
    handleDisableConfirm,
    handleDisableCancel,
    pendingDisableRow,
    isStatusUpdateLoading,
  } = useCustomFieldsTabModel({ channel });

  if (!channel?._id) {
    return (
      <div className={styles.container}>
        <div className={styles.section}>
          <P className={styles.noOrganizationMessage}>
            {t(NO_ORGANIZATION_KEY)}
          </P>
        </div>
      </div>
    );
  }

  return (
    <>
      <CustomFieldsListLayout
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        filteredRows={filteredRows}
        isListLoading={isListLoading}
        configsCalled={configsCalled}
        configsError={configsError}
        onCreateClick={navigateToCreate}
        onSelectRow={navigateToDetail}
        onToggleStatus={handleToggleStatus}
        isStatusUpdateLoading={isStatusUpdateLoading}
      />
      <DisableCustomFieldConfirmationModal
        isOpen={!!pendingDisableRow}
        isLoading={isStatusUpdateLoading}
        onConfirm={handleDisableConfirm}
        onCancel={handleDisableCancel}
      />
    </>
  );
}
